import { useAuth } from '@hb/react'
import { DOT_PHRASE_TEMPLATES, DotPhraseTemplate, MIDWIVES, USERS, WithId } from '@hb/shared'
import { collection, CollectionReference } from 'firebase/firestore'
import { useMemo } from 'react'
import { db } from '../../../backend/db/db'
import { useQuery } from '../useQuery'

export const useDotPhraseTemplates = (
  practiceId: string | null | undefined,
): WithId<DotPhraseTemplate>[] => {
  const currentUserId = useAuth(s => s.authUser?.uid)

  const globalRef = useMemo(
    () => collection(db, DOT_PHRASE_TEMPLATES) as CollectionReference<DotPhraseTemplate>,
    [],
  )

  const practiceRef = useMemo(() => {
    if (!practiceId) return null
    return collection(
      db,
      MIDWIVES,
      practiceId,
      DOT_PHRASE_TEMPLATES,
    ) as CollectionReference<DotPhraseTemplate>
  }, [practiceId])

  const personalRef = useMemo(() => {
    if (!currentUserId) return null
    return collection(
      db,
      USERS,
      currentUserId,
      DOT_PHRASE_TEMPLATES,
    ) as CollectionReference<DotPhraseTemplate>
  }, [currentUserId])

  const { dataArr: globalTemplates } = useQuery<DotPhraseTemplate>(globalRef)
  const { dataArr: practiceTemplates } = useQuery<DotPhraseTemplate>(practiceRef)
  const { dataArr: personalTemplates } = useQuery<DotPhraseTemplate>(personalRef)

  return useMemo(() => {
    // Personal overrides practice overrides global — deduplicate by keyword
    const seen = new Set<string>()
    const merged: WithId<DotPhraseTemplate>[] = []

    for (const t of personalTemplates ?? []) {
      if (!seen.has(t.keyword)) {
        seen.add(t.keyword)
        merged.push(t)
      }
    }
    for (const t of practiceTemplates ?? []) {
      if (!seen.has(t.keyword)) {
        seen.add(t.keyword)
        merged.push(t)
      }
    }
    for (const t of globalTemplates ?? []) {
      if (!seen.has(t.keyword)) {
        seen.add(t.keyword)
        merged.push(t)
      }
    }
    return merged
  }, [globalTemplates, practiceTemplates, personalTemplates])
}
